import { State, process } from '@progress/kendo-data-query';
import { isBefore } from 'date-fns';
import { TFunction } from 'i18next';
import { isEqual } from 'lodash';
import { FC, ReactNode, createContext, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CheckbookCategoryOption,
  EditableColumnCategories,
  initialEditablePeriodColumns,
} from 'src/assets/static/view_checkbook/useCheckbookCategories';
import useCheckbookPeriodColumns from 'src/assets/static/view_checkbook/useCheckbookPeriodColumns';
import { DefaultTransferListDataType } from 'src/components/TransferList';
import useAuth from 'src/hooks/useAuth';
import useBoolean from 'src/hooks/useBoolean';
import useConfirm from 'src/hooks/useConfirm';
import useLocalStorage from 'src/hooks/useLocalstorage';
import useViewCheckbook from 'src/hooks/useViewCheckbook';
import { initialPeriodCheckbookDataState } from 'src/sections/view_checkbook/ViewCheckbookContext';
import { ApprovalStatus, GLCodePeriodBudgetResponse } from 'src/services/useGlCodePeriodBudgetApi';
import { IFilters } from 'src/types/PostBody';
import { SetState } from 'src/types/generic';
import { removeTime } from 'src/utils/formatTime';
import { modifyRequest } from 'src/utils/requestInterceptor';
import { GLPeriodDropdownChildren } from 'src/utils/transformation';
import {
  CHECKBOOK_PERIOD_SELECTION_FILTER_INITIAL_STATE,
  PERIOD_DATE_FIELDS,
  PERIOD_STRING_FIELDS,
} from './ViewPeriodCheckbookTable';
import { viewCheckbookFilters } from './viewCheckbookFilters';

interface PeriodViewContextType {
  isRightBtnDropDwnDisabled: boolean;
  isLeftBtnDropDwnDisabled: boolean;
  onLeftBtnDropDwnClick: VoidFunction;
  onRightBtnDropDwnClick: VoidFunction;
  adjustBudgetPercentage: string;
  setAdjustBalancePercentage: SetState<string>;
  dropdownPeriodFilter: CheckbookCategoryOption;
  setDropdownPeriodFilter: SetState<CheckbookCategoryOption>;
  periodViewTotal: number;
  getPeriodFilters: (state: State) => IFilters[];
  getPeriodSorts: (state: State) => any[];
  periodViewData: GLCodePeriodBudgetResponse[];
  editableColumnDropdownData: EditableColumnCategories[];
  setEditableColumnDropdownData: (value: EditableColumnCategories[]) => void;
  isCurrentPeriodPassed: boolean;
  isGLCodeAtrPanelOpen: boolean;
  glCodeAtrPanelOpen: () => void;
  closeGLCodeAtrPanelOpen: () => void;
  handleCancelSidePanel: VoidFunction;
  t: TFunction<'translation', undefined>;
  isBulkManageGLCode: boolean;
  setIsBulkMangeGLCode: SetState<boolean>;
  selectedRows: GLCodePeriodBudgetResponse[];

  selectedGLCode: GLCodePeriodBudgetResponse[];
  setSelectedGLCode: SetState<GLCodePeriodBudgetResponse[]>;

  invoiceAssignedUsers: DefaultTransferListDataType[];
  setInvoiceAssignedUsers: SetState<DefaultTransferListDataType[]>;
  invoiceUnassignedUsers: DefaultTransferListDataType[];
  setInvoiceUnassignedUsers: SetState<DefaultTransferListDataType[]>;

  poAssignedUsers: DefaultTransferListDataType[];
  setPoAssignedUsers: SetState<DefaultTransferListDataType[]>;
  poUnassignedUsers: DefaultTransferListDataType[];
  setPoUnassignedUsers: SetState<DefaultTransferListDataType[]>;

  invoiceAttributesOption: AttributesOption;
  setInvoiceAttributesOption: SetState<AttributesOption>;
  poAttributesOption: AttributesOption;
  setPoAttributesOption: SetState<AttributesOption>;
  transferListUsers: DefaultTransferListDataType[];

  isManageGLCodeAttributesDirty: boolean;
  isPoWorkFlowDirty: boolean;
  isInvoiceWorkFlowDirty: boolean;
  isSelectedGLCodeEmpty: boolean;
  setOriginAttributeOptions: SetState<Attribute>;
  confirmBeforeProceedingToMainScreen: (acceptCallback: VoidFunction) => void;
  handleGLCodeAttributes: (value: any) => void;
  handleBulkGLCodeAttribute: () => void;
  isImportExportPanelOpen: boolean;
  importExportPanelOpen: () => void;
  closeImportExportPanelOpen: () => void;
  setInitialSelectedGLCode: SetState<GLCodePeriodBudgetResponse[]>;
  updateAdjustBalancePercentageErrorMessage: string[];
  adjustBalancePercentageErrors: {
    isFieldEmpty: boolean;
    isZero: boolean;
  };
}

export const PeriodViewContext = createContext<PeriodViewContextType>({} as PeriodViewContextType);

export type AttributesOption = 'OFF' | 'Exempt' | 'Required';

export type Attribute = {
  poAttribute: AttributesOption;
  invoiceAttribute: AttributesOption;
};

interface PeriodViewContextProps {
  children: ReactNode;
}

const PeriodViewContextProvider: FC<PeriodViewContextProps> = ({ children }) => {
  const { user } = useAuth();
  const { confirm } = useConfirm();
  const [selectedGLCode, setSelectedGLCode] = useState<GLCodePeriodBudgetResponse[]>([]);

  const isSelectedGLCodeEmpty = selectedGLCode.length === 0;
  const {
    value: isGLCodeAtrPanelOpen,
    setTrue: glCodeAtrPanelOpen,
    setFalse: closeGLCodeAtrPanelOpen,
  } = useBoolean(false);

  const [invoiceAssignedUsers, setInvoiceAssignedUsers] = useState<DefaultTransferListDataType[]>(
    []
  );
  const [invoiceUnassignedUsers, setInvoiceUnassignedUsers] = useState<
    DefaultTransferListDataType[]
  >([]);
  const [invoiceAttributesOption, setInvoiceAttributesOption] = useState<AttributesOption>('OFF');
  const [poAssignedUsers, setPoAssignedUsers] = useState<DefaultTransferListDataType[]>([]);
  const [poUnassignedUsers, setPoUnassignedUsers] = useState<DefaultTransferListDataType[]>([]);
  const [poAttributesOption, setPoAttributesOption] = useState<AttributesOption>('OFF');
  const [originalAttributeOptions, setOriginAttributeOptions] = useState<Attribute>({
    poAttribute: ApprovalStatus[0],
    invoiceAttribute: ApprovalStatus[0],
  });

  const {
    dropDownPeriodValue,
    dropDownPeriodValues,
    setDropDownPeriodValue,
    setPeriodCheckbookDataState,
    glCodePeriodBudget,
    periodCheckbookDataState,
    isEditMode,
    editingGlCodePeriodBudget,
    setEditingGlCodePeriodBudget,
    isPeriodView,
    setOriginalGlCodePeriodBudget,
    confirmBeforeProceeding,
    clients,
  } = useViewCheckbook();

  const viewPeriodCheckbookColumns = useCheckbookPeriodColumns(isEditMode, false);
  const [dropdownPeriodFilter, setDropdownPeriodFilter] = useState(
    CHECKBOOK_PERIOD_SELECTION_FILTER_INITIAL_STATE
  );
  const [adjustBudgetPercentage, setAdjustBalancePercentage] = useState('100');
  const { t } = useTranslation();

  const editablePeriodColumns = useMemo(() => {
    const editableFields = initialEditablePeriodColumns.map((c) => c.field);
    let editablePeriodColumns: string[] = [];
    viewPeriodCheckbookColumns.forEach((item) => {
      if (item.field === 'budget' || 'forecast') {
        item?.childrenColumn?.forEach((c) => {
          if (editableFields.includes(c.field || '')) {
            editablePeriodColumns.push(c.field as string);
          }
        });
      }
      if (editableFields.includes(item.field || '')) {
        editablePeriodColumns.push(item.field as string);
      }
    });
    return editablePeriodColumns;
  }, [viewPeriodCheckbookColumns]);

  const [editableColumnDropdownData, setEditableColumnDropdownData] = useLocalStorage(
    `checkBookPeriod-view-${user.ClientId}`,
    initialEditablePeriodColumns
  );

  const { value: isBulkManageGLCode, setBool: setIsBulkMangeGLCode } = useBoolean();

  useEffect(() => {
    setEditableColumnDropdownData((prev) =>
      prev.map((item) => {
        const findColumn = editablePeriodColumns.find((field) => field === item.field);
        return { ...item, display: findColumn ? true : false };
      })
    );
  }, [editablePeriodColumns, setEditableColumnDropdownData]);

  const fiscalYearIndex = useMemo(
    () =>
      dropDownPeriodValues?.findIndex(
        (value) => value.fiscalYear === dropDownPeriodValue?.fiscalYear
      ),
    [dropDownPeriodValue?.fiscalYear, dropDownPeriodValues]
  );

  const currentPeriods = useMemo(
    () => dropDownPeriodValues[fiscalYearIndex]?.items as GLPeriodDropdownChildren[],
    [dropDownPeriodValues, fiscalYearIndex]
  );

  const periodIndex = useMemo(
    () =>
      currentPeriods?.findIndex((period) => period.glPeriodId === dropDownPeriodValue?.glPeriodId),
    [currentPeriods, dropDownPeriodValue?.glPeriodId]
  );

  const isCurrentPeriodPassed: boolean = useMemo(() => {
    const currentDate = removeTime(new Date());
    const currentPeriodEndDate = removeTime(new Date(dropDownPeriodValue?.endDate || ''));
    return isBefore(currentPeriodEndDate, currentDate);
  }, [dropDownPeriodValue?.endDate]);

  const onRightBtnDropDwnClick = useCallback(() => {
    confirmBeforeProceeding(() => {
      setDropDownPeriodValue((prev) => {
        if (periodIndex === currentPeriods?.length - 1) {
          const newValue = dropDownPeriodValues[fiscalYearIndex - 1].items[0];
          return newValue;
        } else {
          const newValue = currentPeriods[periodIndex + 1];
          return newValue;
        }
      });

      setPeriodCheckbookDataState((prev) => ({
        ...initialPeriodCheckbookDataState,
        take: prev.take,
      }));
    });
  }, [
    confirmBeforeProceeding,
    currentPeriods,
    dropDownPeriodValues,
    fiscalYearIndex,
    periodIndex,
    setDropDownPeriodValue,
    setPeriodCheckbookDataState,
  ]);

  const onLeftBtnDropDwnClick = useCallback(() => {
    confirmBeforeProceeding(() => {
      setDropDownPeriodValue((prev) => {
        if (periodIndex === 0) {
          const previousPeriod = dropDownPeriodValues[fiscalYearIndex + 1].items;
          const newValue = previousPeriod[previousPeriod?.length - 1];
          return newValue;
        } else {
          const newValue = currentPeriods[periodIndex - 1];
          return newValue;
        }
      });
      setPeriodCheckbookDataState((prev) => ({
        ...initialPeriodCheckbookDataState,
        take: prev.take,
      }));
    });
  }, [
    confirmBeforeProceeding,
    currentPeriods,
    dropDownPeriodValues,
    fiscalYearIndex,
    periodIndex,
    setDropDownPeriodValue,
    setPeriodCheckbookDataState,
  ]);

  const isLeftBtnDropDwnDisabled: boolean = useMemo(() => {
    if (fiscalYearIndex === dropDownPeriodValues?.length - 1 && periodIndex === 0) {
      return true;
    } else return false;
  }, [dropDownPeriodValues?.length, fiscalYearIndex, periodIndex]);

  const isRightBtnDropDwnDisabled: boolean = useMemo(() => {
    if (fiscalYearIndex === 0 && periodIndex === currentPeriods?.length - 1) {
      return true;
    }
    return false;
  }, [currentPeriods?.length, fiscalYearIndex, periodIndex]);

  const adjustBalancePercentageErrors = useMemo(() => {
    const errors = { isFieldEmpty: false, isZero: false };
    if (adjustBudgetPercentage === '') {
      errors.isFieldEmpty = true;
    }
    if (adjustBudgetPercentage.startsWith('-0') || adjustBudgetPercentage.startsWith('0')) {
      errors.isZero = true;
    }
    return errors;
  }, [adjustBudgetPercentage]);

  const updateAdjustBalancePercentageErrorMessage: string[] = useMemo(() => {
    let errorMessages: string[] = [];
    if (adjustBalancePercentageErrors.isFieldEmpty) {
      errorMessages.push('Percentage value must be entered');
    }
    if (adjustBalancePercentageErrors.isZero) {
      errorMessages.push('Percantage value must be greater than zero');
    }
    return errorMessages;
  }, [adjustBalancePercentageErrors.isFieldEmpty, adjustBalancePercentageErrors.isZero]);

  const getPeriodFilters = useCallback(
    (state: State) => {
      const data = {
        ...modifyRequest(state, PERIOD_DATE_FIELDS, undefined, PERIOD_STRING_FIELDS, false),
      };

      data.filters = [
        ...data.filters,
        ...(viewCheckbookFilters[dropdownPeriodFilter.value]?.filters || []),
      ];
      return data.filters;
    },
    [dropdownPeriodFilter.value]
  );

  const getPeriodSorts = useCallback((state: State) => {
    const data = {
      ...modifyRequest(state, PERIOD_DATE_FIELDS, undefined, PERIOD_STRING_FIELDS, false),
    };

    return data.sort;
  }, []);

  const { data: periodViewData, total: periodViewTotal } = useMemo(
    () =>
      process(glCodePeriodBudget, {
        ...periodCheckbookDataState,
        filter: {
          logic: 'and',
          filters: getPeriodFilters(periodCheckbookDataState),
        },
        sort: getPeriodSorts(periodCheckbookDataState),
      }) as {
        data: GLCodePeriodBudgetResponse[];
        total: number;
      },
    [getPeriodFilters, getPeriodSorts, glCodePeriodBudget, periodCheckbookDataState]
  );

  const selectedRows = useMemo(
    () => (isEditMode ? editingGlCodePeriodBudget : periodViewData).filter((item) => item.selected),
    [editingGlCodePeriodBudget, isEditMode, periodViewData]
  );

  useEffect(() => {
    if (isEditMode && isPeriodView) {
      setEditingGlCodePeriodBudget((prev) => {
        const newlyAddedLineItems = [...prev].filter((item) => item.isNewGlCode);
        const allItems = [...newlyAddedLineItems, ...periodViewData];
        setOriginalGlCodePeriodBudget(allItems);
        return allItems;
      });
    }
  }, [
    isEditMode,
    isPeriodView,
    periodViewData,
    setEditingGlCodePeriodBudget,
    setOriginalGlCodePeriodBudget,
  ]);

  const isAttributeOptionDirty = useMemo(() => {
    const currentAttributeOptions: Attribute = {
      poAttribute: poAttributesOption,
      invoiceAttribute: invoiceAttributesOption,
    };
    return !isEqual(originalAttributeOptions, currentAttributeOptions);
  }, [invoiceAttributesOption, originalAttributeOptions, poAttributesOption]);

  const [initialSelectedGLCode, setInitialSelectedGLCode] = useState<GLCodePeriodBudgetResponse[]>(
    []
  );

  const isGlCodesSelectedDirty = useMemo(() => {
    if (initialSelectedGLCode.length === selectedGLCode.length) {
      const item1 = [...initialSelectedGLCode.map((co) => co.id)].sort((a, b) => a - b);
      const item2 = [...selectedGLCode.map((co) => co.id)].sort((a, b) => a - b);
      return !item1.every((obj, index) => isEqual(obj, item2[index]));
    } else return true;
  }, [initialSelectedGLCode, selectedGLCode]);

  const isManageGLCodeAttributesDirty = useMemo(
    () =>
      isAttributeOptionDirty ||
      poAssignedUsers?.some((user) => user.isDirty) ||
      poUnassignedUsers?.some((user) => user.isDirty) ||
      invoiceAssignedUsers?.some((user) => user.isDirty) ||
      invoiceUnassignedUsers?.some((user) => user.isDirty) ||
      isGlCodesSelectedDirty,
    [
      invoiceAssignedUsers,
      invoiceUnassignedUsers,
      isAttributeOptionDirty,
      poAssignedUsers,
      poUnassignedUsers,
      isGlCodesSelectedDirty,
    ]
  );

  const isPoWorkFlowDirty = useMemo(
    () =>
      poAssignedUsers?.some((user) => user.isDirty) ||
      poUnassignedUsers?.some((user) => user.isDirty),
    [poAssignedUsers, poUnassignedUsers]
  );

  const isInvoiceWorkFlowDirty = useMemo(
    () =>
      invoiceAssignedUsers?.some((user) => user.isDirty) ||
      invoiceUnassignedUsers?.some((user) => user.isDirty),
    [invoiceAssignedUsers, invoiceUnassignedUsers]
  );

  const confirmBeforeProceedingToMainScreen = useCallback(
    (acceptCallback: VoidFunction) => {
      if (isManageGLCodeAttributesDirty) {
        confirm({
          title: 'Unsaved Changes',
          content:
            'You have unsaved changes. Please go back (Cancel) to first save them. Otherwise, you can proceed and your changes will be lost.',
          cancelButtonText: 'Cancel',
          confirmButtonText: 'Discard and Proceed',
          width: '725px',
        }).then((isAccepted) => {
          if (isAccepted) {
            acceptCallback();
          }
        });
      } else {
        acceptCallback();
      }
    },
    [confirm, isManageGLCodeAttributesDirty]
  );

  const handleCancelSidePanel = useCallback(() => {
    confirmBeforeProceedingToMainScreen(() => {
      closeGLCodeAtrPanelOpen();
      setIsBulkMangeGLCode(false);
    });
  }, [closeGLCodeAtrPanelOpen, confirmBeforeProceedingToMainScreen, setIsBulkMangeGLCode]);

  const {
    value: isImportExportPanelOpen,
    setTrue: importExportPanelOpen,
    setFalse: closeImportExportPanelOpen,
  } = useBoolean(false);

  const transferListUsers: DefaultTransferListDataType[] = useMemo(
    () =>
      clients.map((client) => ({
        ...client,
        id: client.clientId,
        selected: false,
        isDirty: false,
      })),
    [clients]
  );

  const handleGLCodeAttributes = useCallback(
    (value: any) => {
      setSelectedGLCode([value]);
      setInitialSelectedGLCode([value]);
      setIsBulkMangeGLCode(false);
      setPoAttributesOption(value?.poApproval);
      setInvoiceAttributesOption(value?.invoiceApproval);
      setOriginAttributeOptions({
        poAttribute: value?.poApproval,
        invoiceAttribute: value?.invoiceApproval,
      });
      const { poAssignedUsers, poUnassignedUsers, invoiceAssignedUsers, invoiceUnassignedUsers } =
        transferListUsers.reduce<{
          poAssignedUsers: DefaultTransferListDataType[];
          poUnassignedUsers: DefaultTransferListDataType[];
          invoiceAssignedUsers: DefaultTransferListDataType[];
          invoiceUnassignedUsers: DefaultTransferListDataType[];
        }>(
          (acc, user) => {
            if (value?.poGlApproval?.clientIds?.includes?.(user.id)) {
              acc.poAssignedUsers.push(user);
            } else {
              acc.poUnassignedUsers.push(user);
            }

            if (value?.invoiceGlApproval?.clientIds?.includes?.(user.id)) {
              acc.invoiceAssignedUsers.push(user);
            } else {
              acc.invoiceUnassignedUsers.push(user);
            }
            return acc;
          },
          {
            poAssignedUsers: [],
            poUnassignedUsers: [],
            invoiceAssignedUsers: [],
            invoiceUnassignedUsers: [],
          }
        );

      setPoAssignedUsers(poAssignedUsers);
      setInvoiceAssignedUsers(invoiceAssignedUsers);
      setPoUnassignedUsers(poUnassignedUsers);
      setInvoiceUnassignedUsers(invoiceUnassignedUsers);
    },
    [setIsBulkMangeGLCode, transferListUsers]
  );

  const handleBulkGLCodeAttribute = useCallback(() => {
    setIsBulkMangeGLCode(true);
    setPoAttributesOption(ApprovalStatus[0]);
    setInvoiceAttributesOption(ApprovalStatus[0]);
    setPoAssignedUsers([]);
    setInvoiceAssignedUsers([]);
    setPoUnassignedUsers(transferListUsers);
    setInvoiceUnassignedUsers(transferListUsers);
    setOriginAttributeOptions({
      poAttribute: ApprovalStatus[0],
      invoiceAttribute: ApprovalStatus[0],
    });
  }, [setIsBulkMangeGLCode, transferListUsers]);

  return (
    <PeriodViewContext.Provider
      value={{
        adjustBalancePercentageErrors,
        isRightBtnDropDwnDisabled,
        isLeftBtnDropDwnDisabled,
        onLeftBtnDropDwnClick,
        onRightBtnDropDwnClick,
        dropdownPeriodFilter,
        setDropdownPeriodFilter,
        periodViewTotal,
        getPeriodFilters,
        getPeriodSorts,
        periodViewData,
        adjustBudgetPercentage,
        setAdjustBalancePercentage,
        editableColumnDropdownData,
        setEditableColumnDropdownData,
        isCurrentPeriodPassed,
        isGLCodeAtrPanelOpen,
        glCodeAtrPanelOpen,
        closeGLCodeAtrPanelOpen,
        handleCancelSidePanel,
        t,
        isBulkManageGLCode,
        setIsBulkMangeGLCode,
        selectedRows,
        selectedGLCode,
        setSelectedGLCode,
        invoiceAssignedUsers,
        setInvoiceAssignedUsers,
        invoiceUnassignedUsers,
        setInvoiceUnassignedUsers,
        poAssignedUsers,
        setPoAssignedUsers,
        poUnassignedUsers,
        setPoUnassignedUsers,
        invoiceAttributesOption,
        setInvoiceAttributesOption,
        poAttributesOption,
        setPoAttributesOption,
        transferListUsers,
        isManageGLCodeAttributesDirty,
        isSelectedGLCodeEmpty,
        isPoWorkFlowDirty,
        isInvoiceWorkFlowDirty,
        setOriginAttributeOptions,
        confirmBeforeProceedingToMainScreen,
        handleGLCodeAttributes,
        handleBulkGLCodeAttribute,
        importExportPanelOpen,
        isImportExportPanelOpen,
        closeImportExportPanelOpen,
        setInitialSelectedGLCode,
        updateAdjustBalancePercentageErrorMessage,
      }}
    >
      {children}
    </PeriodViewContext.Provider>
  );
};

export default PeriodViewContextProvider;
